import { useEffect, useRef, useState } from 'react';
import { Settings, X, CheckCircle, Info } from 'react-feather';
import {
  getDetectionConfigHandler,
  saveDetectionConfigHandler,
  replaceDetectionConfigHandler,
  saveLocalDetectionFlags,
} from '../../services/documentForgery';

// ── Feature definitions (mirrors backend DETECTION_FEATURES) ─────────────────
// Kept in sync with models.py — update both if adding new features.
const FEATURE_DEFS = {
  feature_extraction:     { label: 'Feature Extraction',     group: 'core',     desc: 'Extract invoice fields: vendor, dates, amounts, line items using GPT-4.1 Vision' },
  numeric_reconciliation: { label: 'Numeric Reconciliation', group: 'core',     desc: 'Cross-check totals, taxes, and line-item arithmetic for discrepancies' },
  date_checks:            { label: 'Date Consistency',       group: 'core',     desc: 'Detect future-dated, mismatched, or implausible dates' },
  duplicate_detection:    { label: 'Duplicate Line Items',   group: 'core',     desc: 'Flag repeated items within the same invoice' },
  words_vs_figures:       { label: 'Amount in Words Check',  group: 'core',     desc: 'Verify total-in-words matches the numeric total' },
  metadata_forensics:     { label: 'PDF Metadata Forensics', group: 'core',     desc: 'Detect editing-software traces and date anomalies in PDF metadata' },
  qr_barcode_scan:        { label: 'QR / Barcode Scan',      group: 'core',     desc: 'Detect and decode QR codes and barcodes; flag suspicious UPI payment links' },
  risk_summary:           { label: 'AI Risk Summary',        group: 'core',     desc: 'Generate a plain-language risk summary using the LLM after all checks' },
  gst_verification:       { label: 'GST Verification',       group: 'advanced', desc: 'Validate GSTIN format and fuzzy-match vendor name against GST registry (API call)' },
  llm_forgery_analysis:   { label: 'LLM Visual Forgery',     group: 'advanced', desc: 'GPT-4.1 Vision analysis of layout, font, and contextual anomalies (costs tokens)' },
  signature_check:        { label: 'Signature Analysis',     group: 'advanced', desc: 'Detect multiple signature regions and cross-validate with LLM findings' },
  font_manipulation:      { label: 'Font Manipulation',      group: 'advanced', desc: 'Pixel-level ink density analysis to detect overprinted or replaced text fields' },
  handwriting_analysis:   { label: 'Handwriting Analysis',   group: 'advanced', desc: 'Stroke complexity analysis to detect digit alteration in handwritten regions' },
  low_confidence_cluster:  { label: 'Paste-over Detection',   group: 'advanced', desc: 'Cluster low-confidence OCR words to identify erased or pasted regions' },
  custom_fields_detection: { label: 'Custom Fields Detection', group: 'advanced', desc: 'Extract and validate custom fields configured for this document type' },
};

// ── localStorage helpers delegated to the service layer ──────────────────────
const loadFlags = (docType) => {
  try {
    const raw = localStorage.getItem(`insightai_detection_flags_${docType}`);
    if (raw) return JSON.parse(raw);
  } catch {}
  return Object.fromEntries(Object.keys(FEATURE_DEFS).map(k => [k, true]));
};

// saveFlags = saveLocalDetectionFlags from service (imported above)
const saveFlags = (docType, flags) => saveLocalDetectionFlags(docType, flags);

// ── Cost estimate ─────────────────────────────────────────────────────────────
const estimateCost = (flags) => {
  // Rough relative cost units (feature_extraction baseline = 1)
  const costs = {
    feature_extraction:     1.0,
    llm_forgery_analysis:   1.2,
    risk_summary:           0.4,
    gst_verification:       0.1,
    numeric_reconciliation: 0.0,
    date_checks:            0.0,
    duplicate_detection:    0.0,
    words_vs_figures:       0.0,
    metadata_forensics:     0.0,
    qr_barcode_scan:        0.05,
    signature_check:        0.0,
    font_manipulation:      0.1,
    handwriting_analysis:   0.1,
    low_confidence_cluster: 0.0,
  };
  const total = Object.entries(flags)
    .filter(([, v]) => v)
    .reduce((sum, [k]) => sum + (costs[k] || 0), 0);
  const max = Object.values(costs).reduce((s, v) => s + v, 0);
  const pct = Math.round((total / max) * 100);
  if (pct >= 90) return { label: 'Full analysis', color: '#7c3aed', pct };
  if (pct >= 60) return { label: 'Standard analysis', color: '#2563eb', pct };
  if (pct >= 30) return { label: 'Lite analysis', color: '#16a34a', pct };
  return { label: 'Minimal', color: '#9ca3af', pct };
};

// ── Toggle switch ─────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange, disabled }) => (
  <div
    onClick={() => !disabled && onChange(!checked)}
    style={{
      width: 36, height: 20, borderRadius: 10, flexShrink: 0,
      background: checked ? '#44ace4' : '#d1d5db',
      position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background 0.2s', opacity: disabled ? 0.5 : 1,
    }}
  >
    <div style={{
      position: 'absolute',
      top: 2, left: checked ? 18 : 2,
      width: 16, height: 16, borderRadius: '50%',
      background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      transition: 'left 0.2s',
    }} />
  </div>
);

// ── Tooltip ───────────────────────────────────────────────────────────────────
const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <Info size={12} color="#9ca3af" style={{ cursor: 'help' }} />
      {show && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%',
          transform: 'translateX(-50%)', marginBottom: 6,
          background: '#1f2937', color: '#fff',
          padding: '6px 10px', borderRadius: 6, fontSize: 11,
          width: 220, lineHeight: 1.5, zIndex: 100,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          whiteSpace: 'normal', pointerEvents: 'none',
        }}>
          {text}
        </div>
      )}
    </div>
  );
};

// ── Feature row ───────────────────────────────────────────────────────────────
const FeatureRow = ({ featureKey, def, checked, onChange, alwaysOn }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '9px 0', borderBottom: '1px solid #f3f4f6',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
          {def.label}
          {alwaysOn && (
            <span style={{ marginLeft: 6, fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>
              (always on)
            </span>
          )}
        </div>
      </div>
      <InfoTooltip text={def.desc} />
    </div>
    <div style={{ marginLeft: 12 }}>
      <Toggle checked={checked} onChange={onChange} disabled={alwaysOn} />
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const DetectionSettings = ({ docType = 'INVOICE' }) => {
  const [open,    setOpen]    = useState(false);
  const [flags,   setFlags]   = useState(() => loadFlags(docType));
  const [loaded,  setLoaded]  = useState(false);   // true once server fetch done
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');
  const modalRef = useRef(null);

  // Always-on: Feature extraction is the foundation everything else reads from
  const ALWAYS_ON = new Set(['feature_extraction']);

  // ── Fetch from server on mount ────────────────────────────────────────
  // If server returns features=null  → never saved, keep localStorage as-is
  // If server returns features={...} → user has saved config, use that
  useEffect(() => {
    getDetectionConfigHandler(docType)
      .then(res => {
        const serverFeatures = res?.data?.features;
        if (serverFeatures && typeof serverFeatures === 'object'
            && !res?.data?.is_default
            && Object.keys(serverFeatures).length > 0) {
          // Server has a real saved config — use it and mirror to localStorage
          setFlags(serverFeatures);
          saveFlags(docType, serverFeatures);
        }
        // If null / is_default=true → keep whatever is in localStorage already
      })
      .catch(() => {
        // Network error — silently keep localStorage state
      })
      .finally(() => setLoaded(true));
  }, [docType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleFlag = (key) => {
    if (ALWAYS_ON.has(key)) return;
    setFlags(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveFlags(docType, next);    // persist to localStorage immediately
      return next;
    });
    setSaved(false);
  };

  const handleSelectAll   = (group) => {
    setFlags(prev => {
      const next = { ...prev };
      Object.entries(FEATURE_DEFS).forEach(([k, d]) => {
        if (d.group === group && !ALWAYS_ON.has(k)) next[k] = true;
      });
      saveFlags(docType, next);
      return next;
    });
    setSaved(false);
  };

  const handleDeselectAll = (group) => {
    setFlags(prev => {
      const next = { ...prev };
      Object.entries(FEATURE_DEFS).forEach(([k, d]) => {
        if (d.group === group && !ALWAYS_ON.has(k)) next[k] = false;
      });
      saveFlags(docType, next);
      return next;
    });
    setSaved(false);
  };

  // (Server sync happens on mount — no extra fetch needed on open)

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      // Use replaceDetectionConfigHandler (POST) for full replace, not PATCH merge
      // This ensures user's exact selections are stored, not merged with defaults
      await replaceDetectionConfigHandler(docType, flags);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError('Saved locally. Could not reach server — try again later.');
    } finally {
      setSaving(false);
    }
  };

  const groups = {
    core:     { label: 'Core Checks',     subtitle: 'Recommended for all documents. Fast and low cost.' },
    advanced: { label: 'Advanced Checks', subtitle: 'Additional LLM / API calls. Higher accuracy, higher cost.' },
  };

  const cost = estimateCost(flags);
  const enabledCount = Object.values(flags).filter(Boolean).length;

  return (
    <>
      {/* ── Gear trigger button ── */}
      <button
        onClick={() => setOpen(true)}
        title="Configure detection settings"
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '6px 12px', borderRadius: 7,
          background: '#f0f7ff', border: '1px solid #d8eef8',
          color: '#44ace4', fontWeight: 600, fontSize: 12,
          cursor: 'pointer', whiteSpace: 'nowrap',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#e0f0fc'}
        onMouseLeave={e => e.currentTarget.style.background = '#f0f7ff'}
      >
        <Settings size={13} />
        Detection Settings
        <span style={{
          background: '#44ace4', color: '#fff',
          borderRadius: 20, fontSize: 10, fontWeight: 800,
          padding: '1px 6px', lineHeight: 1.4,
        }}>
          {enabledCount}
        </span>
      </button>

      {/* ── Modal backdrop ── */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}>
          <div
            ref={modalRef}
            style={{
              background: '#fff', borderRadius: 14,
              width: '100%', maxWidth: 520,
              maxHeight: '88vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              overflow: 'hidden',
            }}
          >
            {/* ── Modal header ── */}
            <div style={{
              padding: '18px 20px 14px',
              borderBottom: '1px solid #f3f4f6',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>
                  Detection Settings
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                  Choose which checks to run for <strong>{docType}</strong> documents
                </div>
              </div>
              <button onClick={() => setOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#9ca3af', display: 'flex' }}>
                <X size={18} />
              </button>
            </div>

            {/* ── Cost indicator ── */}
            <div style={{
              padding: '10px 20px', background: '#f9fafb',
              borderBottom: '1px solid #f3f4f6', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: cost.color }}>{cost.label}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{enabledCount} / {Object.keys(FEATURE_DEFS).length} checks</span>
                </div>
                <div style={{ height: 5, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${cost.pct}%`,
                    background: cost.color, borderRadius: 99,
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              </div>
            </div>

            {/* ── Scrollable body ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px 0' }}>
              {Object.entries(groups).map(([groupKey, groupDef]) => {
                const groupFeatures = Object.entries(FEATURE_DEFS).filter(([, d]) => d.group === groupKey);
                const allOn  = groupFeatures.every(([k]) => ALWAYS_ON.has(k) || flags[k]);
                const allOff = groupFeatures.every(([k]) => ALWAYS_ON.has(k) || !flags[k]);
                return (
                  <div key={groupKey} style={{ paddingBottom: 4 }}>
                    {/* Group header */}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                      paddingTop: 16, paddingBottom: 6,
                      position: 'sticky', top: 0, background: '#fff', zIndex: 2,
                    }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {groupDef.label}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{groupDef.subtitle}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                        <button onClick={() => handleSelectAll(groupKey)}
                          style={{ fontSize: 11, color: '#44ace4', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                          All on
                        </button>
                        <span style={{ color: '#e5e7eb' }}>|</span>
                        <button onClick={() => handleDeselectAll(groupKey)}
                          style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                          All off
                        </button>
                      </div>
                    </div>
                    {/* Feature rows */}
                    {groupFeatures.map(([key, def]) => (
                      <FeatureRow
                        key={key}
                        featureKey={key}
                        def={def}
                        checked={!!flags[key]}
                        onChange={() => toggleFlag(key)}
                        alwaysOn={ALWAYS_ON.has(key)}
                      />
                    ))}
                  </div>
                );
              })}
              <div style={{ height: 16 }} />
            </div>

            {/* ── Footer ── */}
            <div style={{
              padding: '12px 20px', borderTop: '1px solid #f3f4f6',
              flexShrink: 0, background: '#fff',
            }}>
              {error && (
                <div style={{ fontSize: 11, color: '#d97706', marginBottom: 8 }}>⚠ {error}</div>
              )}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ flex: 1, fontSize: 11, color: '#9ca3af' }}>
                  Changes apply to future uploads. Settings saved locally until synced.
                </div>
                <button onClick={() => setOpen(false)}
                  style={{ padding: '8px 14px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 13, fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}>
                  Close
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '8px 18px', borderRadius: 7, border: 'none',
                    background: saved ? '#16a34a' : '#44ace4', color: '#fff',
                    fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                    transition: 'background 0.2s',
                  }}
                >
                  {saved ? <><CheckCircle size={13} /> Saved!</> : saving ? 'Saving…' : 'Save to Server'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DetectionSettings;